import { useCallback, useRef, useState } from "react";

/**
 * Measures a DOM node's real rendered height and keeps it in sync via
 * ResizeObserver. Needed for `position: fixed` panels whose height varies
 * with content (e.g. translated text length) — a hardcoded pixel offset
 * for whatever sits below them drifts out of sync and clips content.
 *
 * Uses a callback ref (not a plain ref + mount-effect) because several
 * callers attach this ref inside a branch that only renders after an
 * initial loading state — a mount-once effect would miss that node
 * entirely since it fires before the real element exists.
 */
export function useElementHeight<T extends HTMLElement>() {
  const [height, setHeight] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    setHeight(node.getBoundingClientRect().height);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setHeight(entry.contentRect.height);
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return [ref, height] as const;
}
